import { InverseHeader } from '@jouzen/ecom-components/layout';
import { Typography } from '@jouzen/ecom-components/v3/Typography';

import { EventType } from '@/analytics/types';
import AccessoryCard from '@/app/components/AccessoryCard';
import RichText from '@/app/components/RichText';
import { DEXCOM_STELO_KEY } from '@/app/consts/productKeys';
import useCurrency from '@/app/hooks/useCurrency';
import useIsNomadEnabled from '@/app/hooks/useIsNomadEnabled';
import useIsSteloEnabled from '@/app/hooks/useIsSteloEnabled';
import useUsaOnly from '@/app/hooks/useUsaOnly';
import { useProductsData } from '@/queries/Products';
import type { ListProduct } from '@/queries/utils/mapProductFragmentToListProduct';

import _accessoriesData, { NOMAD_ACCESSORY } from './_data/accessoriesData';

const filterProductByGateValues = (
  productKey: string,
  isSteloEnabled: boolean,
  isUSAOnly: boolean,
) => {
  if (productKey === DEXCOM_STELO_KEY) {
    return isSteloEnabled;
  } else if (productKey === 'OuraGiftCard') {
    return isUSAOnly;
  } else {
    return true;
  }
};

const AccessoriesSection = () => {
  const { currencyCode } = useCurrency();
  const isSteloEnabled = useIsSteloEnabled();
  const isUSAOnly = useUsaOnly();
  const isNomadEnabled = useIsNomadEnabled();

  const handleClick = (path: string) => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'shop',
      location: 'accessories',
      path,
      action: 'go to pdp',
    });
  };

  const accessoriesData = isNomadEnabled
    ? [NOMAD_ACCESSORY, ..._accessoriesData]
    : _accessoriesData;

  // Pre-filter product keys to only include enabled products to prevent over-fetching
  const productKeys = accessoriesData
    .map((accessory) => accessory.productKey)
    .filter((productKey) => {
      return filterProductByGateValues(productKey, isSteloEnabled, isUSAOnly);
    });

  const { data: productsData } = useProductsData({
    includeInventory: false,
    productKeys,
  });

  const productsQueryDataByKey = productsData?.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.productKey]: cur,
    }),
    {} as Record<string, ListProduct>,
  );

  const accessoryProducts = accessoriesData
    // Filtering must still be done here since we filter on the hardcoded
    // product data and not on the response itself
    .filter(({ productKey }) =>
      filterProductByGateValues(productKey, isSteloEnabled, isUSAOnly),
    )
    .map(({ priceData, ...accessory }) => {
      const product = productsQueryDataByKey?.[accessory.productKey];

      // use hardcoded price data if product is not part of catalogue (e.g. gift card)
      const accessoryPriceData =
        !product && priceData
          ? priceData
          : {
              discountedPrice: product?.discountedPrice.discountedPrice?.amount,
              basePrice: product?.price?.amount,
              currencyCode,
            };

      return {
        ...accessory,
        ...accessoryPriceData,
      };
    });

  return (
    // brings back to dark mode after scrolling past section with InverseHeader exit={true}
    <InverseHeader>
      <section
        className="flex flex-col items-center px-6 pt-6 md:px-15 md:pt-20"
        data-cy="accessories-section"
      >
        <Typography
          Element="h2"
          font="serif"
          className="pb-6 text-center text-3xl md:pb-4 md:text-5xl lg:text-6xl xxl:pb-16"
        >
          <RichText messageKey="accessories_to_complete_experience" />
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8 xxl:grid-cols-3">
          {accessoryProducts.map(({ productKey, ...accessoryProduct }) => (
            <AccessoryCard
              key={productKey}
              {...accessoryProduct}
              onClick={() => handleClick(accessoryProduct.href)}
            />
          ))}
        </div>
      </section>
    </InverseHeader>
  );
};

export default AccessoriesSection;
